import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../../assets";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import GlobalButton from "../../../../components/CustomButton";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import TextInput from "../../../../components/TextInputs";
import useDebounce from "../../../../hooks/useDebounce";
import {
    setBlogTopicCurrentPage,
    setBlogTopicPageLimit,
    setBlogTopicTotalCount,
    setBlogTopicTotalPages,
} from "../../../../lib/slices/pagination/blogTopicPageSlice";
import { setBlogTopic } from "../../../../lib/slices/searchKeysSlice";
import { getBlogTopics, updateBlogTopic } from "../../../../services/blogTopicApiFn";
import { handleOpenSnackBar } from "../../../../utils";
import { AddEditBlogTopicDialog } from "./AddEditBlogTopicDialog";

// Header Data For Promotional Block Table
const headerData = [
  "createdAt",
  "name",
  "description",
  "status",
  "featured",
  "actions",
];

// Table Rows For Promotional Block Table
const tableRows = [
  "created_date",
  "blog_topic_name",
  "blog_topic_description",
  "status",
  "featured",
  "actions",
];

// Promotional Block Component
export function BlogTopic() {
  // Dispatch Hook
  const dispatch = useDispatch();
  // Open Dialog Ref
  const openDialog = useRef(null);
  // Loading State
  const [isLoading, setIsLoading] = useState(true);
  // All Promotional Blocks
  const [allBlogTopics, setAllBlogTopics] = useState([]);
  // Translation Hook
  const { t } = useTranslation(["catalogue", "common"]);
  // Search Keys
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  // Get Pagination for Promotional Blocks
  const pagination = useSelector((state) => state.blogTopicPaginationReducer);
  // Debounced Search Term
  const debouncedSearchTerm = useDebounce(searchKeys.blogTopic, 1000, true, () => {
    dispatch(setBlogTopicCurrentPage(1));
  });

  // Fetch All Blog Topics
  const handleFetchAllBlogTopics = async (search, page, limit, loading = true) => {
    try {
      const params = {
        search,
        page,
        limit,
      };
      setIsLoading(loading);
      const response = await getBlogTopics(params);
      if (response.data.status === "success") {
        let data = response?.data.data?.data;
        const pagination = response?.data.data.pagination;
        setAllBlogTopics(data);
        dispatch(setBlogTopicPageLimit(pagination.limit));
        dispatch(setBlogTopicCurrentPage(pagination.page));
        dispatch(setBlogTopicTotalCount(pagination.total));
        dispatch(setBlogTopicTotalPages(pagination.totalPages));
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch All Promotional Blocks On Component Mount
  useEffect(() => {
    handleFetchAllBlogTopics(
      debouncedSearchTerm,
      pagination.page,
      pagination.limit,
    );
  }, [debouncedSearchTerm, pagination.page, pagination.limit]);

  // Handle Edit Promotional Block
  const handleEdit = (params) => {
    console.log(params, "sdskadaskjdasjkdsjakdjkasdjksajkdjksadjkasjk");
    openDialog.current.openDialog({
      type: "edit",
      data: params.row,
      getData: handleFetchAllBlogTopics,
    });
  };

  // handle page change
  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setBlogTopicCurrentPage(page));
  };

  // handle rows per page change
  const handleRowsPerPageChange = (event) => {
    console.log(event, "event");
    const limit = parseInt(event.target.value, 10);
    dispatch(setBlogTopicPageLimit(limit));
    dispatch(setBlogTopicCurrentPage(1));
  };

  // Handle Change Status Promotional Block
  const handleChangeStatus = async (event, row, key) => {
    try {
      let payload = {
        [key]: event,
      };
      const response = await updateBlogTopic(row._id, payload);
      if (response.data.status === "success") {
        handleFetchAllBlogTopics(
          searchKeys.blogTopic,
          pagination.page,
          pagination.limit,
          false,
        );
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    }
  };

  // Prepare Export Data For Promotional Block
  const prepareExportData = () => {
    return allBlogTopics?.map((item) => ({
      "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
      "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
      "Name In Romanian": item.title?.ro || "-",
      "Name In English": item.title?.en || "-",
      Status: item.status ? "Active" : "Inactive",
    }));
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        gap={1}
        flexGrow={1}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <GlobalButton
          variant={"contained"}
          btnText={"addNew"}
          handleClickBtn={() => {
            openDialog.current.openDialog({
              type: "add",
              data: null,
              getData: handleFetchAllBlogTopics,
            });
          }}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        />
        <CSVLink
          data={prepareExportData()}
          filename={"blog-topic.csv"}
          className="csv-link"
          target="_blank"
        >
          <GlobalButton
            variant={"export_button"}
            btnText={"export"}
            startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
            styleProps={{
              width: "auto",
              height: 40,
            }}
          />
        </CSVLink>
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.blogTopic && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(setBlogTopic(""));
                }}
              />
            )
          }
          placeholder={t("searchByName", { ns: "common" })}
          value={searchKeys.blogTopic}
          onChange={(e) => {
            dispatch(setBlogTopic(e.target.value));
          }}
        />
      </Stack>
    );
  };

  const renderTable = useMemo(() => {
    return (
      <DynamicPaginatedTable
        minWidth={1200}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allBlogTopics || []}
        showActions={true}
        showEdit={true}
        handleEdit={handleEdit}
        handleChangeStatus={(e, row, key) =>
          handleChangeStatus(e, row, "status")
        }
        handleChangeFeatured={(e, row, key) =>
          handleChangeStatus(e, row, "isFeatured")
        }
        paginatedData={pagination}
        showDynamicPagination={true}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 20, 30, 50, 100]}
        isLoading={isLoading}
      />
    );
  }, [allBlogTopics, isLoading]);

  return (
    <React.Fragment>
      <PaperContainer
        title={t("blogTopic")}
        subtitle={t("viewBlogTopic")}
        headerActions={renderHeaderAction()}
        searchActions={renderSearchAction()}
      >
        {renderTable}
      </PaperContainer>

      <AddEditBlogTopicDialog ref={openDialog} />
    </React.Fragment>
  );
}
